import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Archive,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import {
  getCaseEvidencePack,
  getCaseV2Rollup,
  getShipmentCase,
  listShipmentCases,
  setComplianceContext,
  updateCaseActionStatus,
  updateShipmentCaseStatus,
} from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type {
  CaseActionStatus,
  CaseStatus,
  CaseV2RollupResponse,
  ShipmentCaseDetail,
  ShipmentCaseItem,
} from "../../types/compliance";

const statusOptions: Array<{ value: CaseStatus; label: string }> = [
  { value: "ready_for_review", label: "Ready" },
  { value: "approved", label: "Approve" },
  { value: "exception", label: "Exception" },
  { value: "closed", label: "Close" },
  { value: "reopened", label: "Reopen" },
];

const actionStatusOptions: Array<{ value: CaseActionStatus; label: string }> = [
  { value: "in_progress", label: "Start" },
  { value: "resolved", label: "Resolve" },
  { value: "waived", label: "Waive" },
];

function formatDate(value?: string | null) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function statusClass(status: string) {
  if (status === "approved") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "exception") return "bg-red-50 text-red-700 border-red-200";
  if (status === "closed") return "bg-gray-100 text-gray-700 border-gray-200";
  if (status === "reopened") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-blue-50 text-blue-700 border-blue-200";
}

function humanizeStatus(status: string) {
  return status.replace(/_/g, " ");
}

function caseRiskLabel(item: ShipmentCaseItem) {
  if (item.risk_count === 0) return "No risks recorded";
  const highSignal = item.missing_document_count + item.mismatch_count;
  return `${item.risk_count} risk${item.risk_count === 1 ? "" : "s"} / ${highSignal} case issue${highSignal === 1 ? "" : "s"}`;
}

function priorityClass(priority: string) {
  if (priority === "high") return "bg-red-100 text-red-700";
  if (priority === "medium") return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-700";
}

function actionStatusClass(status: string) {
  if (status === "resolved") return "bg-emerald-100 text-emerald-700";
  if (status === "waived") return "bg-gray-100 text-gray-700";
  if (status === "in_progress") return "bg-blue-100 text-blue-700";
  return "bg-amber-100 text-amber-700";
}

function v2DecisionClass(decision: string) {
  if (decision === "ready_for_approval") return "bg-emerald-100 text-emerald-700";
  if (decision === "blocked") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

function scoreClass(score: number) {
  if (score >= 85) return "text-emerald-700";
  if (score >= 60) return "text-amber-700";
  return "text-red-700";
}

export default function ShipmentCaseWorkspace() {
  const { credentials } = useComplianceAuth();
  const tenantId = credentials?.tenantId ?? "default";
  const role = credentials?.role ?? "COMPLIANCE_ADMIN";
  const canChangeStatus = role === "COMPLIANCE_ADMIN";
  const [cases, setCases] = useState<ShipmentCaseItem[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ShipmentCaseDetail | null>(null);
  const [v2Rollup, setV2Rollup] = useState<CaseV2RollupResponse | null>(null);
  const [loadingCases, setLoadingCases] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [updatingAction, setUpdatingAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setComplianceContext(tenantId, role);
    void loadCases();
  }, [tenantId, role]);

  useEffect(() => {
    if (selectedCaseId) void loadDetail(selectedCaseId);
  }, [selectedCaseId]);

  async function loadCases() {
    setLoadingCases(true);
    setError(null);
    try {
      const response = await listShipmentCases();
      setCases(response.items);
      setSelectedCaseId((current) => current ?? response.items[0]?.case_id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load shipment cases");
    } finally {
      setLoadingCases(false);
    }
  }

  async function loadDetail(caseId: string) {
    setLoadingDetail(true);
    setError(null);
    try {
      const response = await getShipmentCase(caseId);
      const rollup = await getCaseV2Rollup(caseId);
      setDetail(response);
      setV2Rollup(rollup);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load case detail");
      setDetail(null);
      setV2Rollup(null);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function changeStatus(status: CaseStatus) {
    if (!detail) return;
    setUpdatingStatus(status);
    setError(null);
    try {
      await updateShipmentCaseStatus(
        detail.case_id,
        status,
        `Status changed from ${detail.status} to ${status}`,
      );
      await Promise.all([loadCases(), loadDetail(detail.case_id)]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update case status");
    } finally {
      setUpdatingStatus(null);
    }
  }

  async function downloadEvidencePack() {
    if (!detail) return;
    setError(null);
    try {
      const pack = await getCaseEvidencePack(detail.case_id);
      const blob = new Blob([JSON.stringify(pack, null, 2)], {
        type: "application/json",
      });
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = `${detail.case_id}-evidence-pack.json`;
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download evidence pack");
    }
  }

  async function changeActionStatus(
    actionId: string,
    status: CaseActionStatus,
  ) {
    if (!detail) return;
    setUpdatingAction(actionId);
    setError(null);
    try {
      await updateCaseActionStatus(
        detail.case_id,
        actionId,
        status,
        `Action moved to ${status}`,
      );
      await loadDetail(detail.case_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update action");
    } finally {
      setUpdatingAction(null);
    }
  }

  const openCases = useMemo(
    () => cases.filter((item) => item.status !== "closed").length,
    [cases],
  );
  const issueCount = useMemo(
    () => cases.reduce((sum, item) => sum + item.missing_document_count + item.mismatch_count, 0),
    [cases],
  );
  const openActionCount = detail?.actions.filter(
    (item) => item.status === "open" || item.status === "in_progress",
  ).length ?? 0;

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-6">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Archive size={24} className="text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Shipment Cases</h1>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Review each shipment batch as a compliance case with its evidence, risk trail, and audit record.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/Compliance/logistics/documents"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            <FileText size={15} />
            Upload Batch
          </Link>
          <button
            type="button"
            onClick={() => void loadCases()}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-black"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-gray-500">Cases</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{cases.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-gray-500">Open Review</p>
          <p className="mt-1 text-2xl font-bold text-blue-700">{openCases}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-gray-500">Case Issues</p>
          <p className="mt-1 text-2xl font-bold text-amber-700">{issueCount}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-gray-500">Open Actions</p>
          <p className="mt-1 text-2xl font-bold text-red-700">{openActionCount}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[420px_1fr]">
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="font-semibold text-gray-900">Case Portfolio</h2>
          </div>
          {loadingCases ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <Loader2 size={18} className="mr-2 inline-block animate-spin" />
              Loading cases...
            </div>
          ) : cases.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              No shipment cases yet. Save or analyze a batch from the document workspace.
            </div>
          ) : (
            <div className="max-h-[620px] divide-y divide-gray-100 overflow-y-auto">
              {cases.map((item) => {
                const selected = item.case_id === selectedCaseId;
                return (
                  <button
                    key={item.case_id}
                    type="button"
                    onClick={() => setSelectedCaseId(item.case_id)}
                    className={`w-full px-4 py-3 text-left transition ${
                      selected ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-mono text-sm font-semibold text-gray-900">
                          {item.case_id}
                        </p>
                        <p className="mt-1 truncate text-xs text-gray-600">
                          {item.shipment_reference || item.batch_name}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusClass(item.status)}`}
                      >
                        {humanizeStatus(item.status)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      {item.document_count} document{item.document_count === 1 ? "" : "s"} · {caseRiskLabel(item)}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {!selectedCaseId ? (
            <div className="p-8 text-sm text-gray-500">Select a case to review.</div>
          ) : loadingDetail ? (
            <div className="p-8 text-center text-sm text-gray-500">
              <Loader2 size={18} className="mr-2 inline-block animate-spin" />
              Loading case workspace...
            </div>
          ) : !detail ? (
            <div className="p-8 text-sm text-gray-500">Case detail is unavailable.</div>
          ) : (
            <div>
              <div className="border-b border-gray-100 px-5 py-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate font-mono text-lg font-bold text-gray-900">
                        {detail.case_id}
                      </h2>
                      <span
                        className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(detail.status)}`}
                      >
                        {humanizeStatus(detail.status)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">
                      Shipment: {detail.shipment_reference || "Not detected"} · Batch: {detail.batch_name}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void downloadEvidencePack()}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800"
                  >
                    <Download size={15} />
                    Evidence Pack
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 border-b border-gray-100 p-5 sm:grid-cols-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">Documents</p>
                  <p className="text-xl font-bold text-gray-900">{detail.document_count}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">Risks</p>
                  <p className="text-xl font-bold text-amber-700">{detail.risk_count}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">Missing Docs</p>
                  <p className="text-xl font-bold text-red-700">{detail.missing_document_count}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-gray-500">Mismatches</p>
                  <p className="text-xl font-bold text-red-700">{detail.mismatch_count}</p>
                </div>
              </div>

              {v2Rollup && (
                <div className="border-b border-gray-100 bg-blue-50/40 p-5">
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">V2 Case Intelligence</h3>
                      <p className="text-xs text-gray-600">
                        Aggregated readiness and consistency across {v2Rollup.workflow_count} workflow{v2Rollup.workflow_count === 1 ? "" : "s"}.
                      </p>
                    </div>
                    <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${v2DecisionClass(v2Rollup.decision)}`}>
                      {humanizeStatus(v2Rollup.decision)}
                    </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                      <p className="text-xs font-semibold uppercase text-gray-500">Shipment</p>
                      <p className={`text-2xl font-bold ${scoreClass(v2Rollup.shipment_readiness_score)}`}>
                        {v2Rollup.shipment_readiness_score}%
                      </p>
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                      <p className="text-xs font-semibold uppercase text-gray-500">Audit</p>
                      <p className={`text-2xl font-bold ${scoreClass(v2Rollup.audit_readiness_score)}`}>
                        {v2Rollup.audit_readiness_score}%
                      </p>
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                      <p className="text-xs font-semibold uppercase text-gray-500">Consistency</p>
                      <p className={`text-2xl font-bold ${scoreClass(Math.round(v2Rollup.consistency_score * 100))}`}>
                        {Math.round(v2Rollup.consistency_score * 100)}%
                      </p>
                    </div>
                    <div className="rounded-lg border border-blue-100 bg-white p-3">
                      <p className="text-xs font-semibold uppercase text-gray-500">Discrepancies</p>
                      <p className="text-2xl font-bold text-red-700">{v2Rollup.discrepancy_count}</p>
                    </div>
                  </div>
                  {v2Rollup.missing_required_documents.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {v2Rollup.missing_required_documents.map((documentType) => (
                        <span key={documentType} className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                          Missing {humanizeStatus(documentType)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {canChangeStatus && (
                <div className="border-b border-gray-100 px-5 py-4">
                  <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                    Case Decision
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        disabled={updatingStatus !== null}
                        onClick={() => void changeStatus(option.value)}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {updatingStatus === option.value ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : option.value === "approved" ? (
                          <CheckCircle2 size={14} />
                        ) : (
                          <ShieldCheck size={14} />
                        )}
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-b border-gray-100 p-5">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-gray-900">Action Register</h3>
                  <span className="text-xs text-gray-500">
                    {openActionCount} open
                  </span>
                </div>
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {detail.actions.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">
                      No compliance actions are open for this case.
                    </p>
                  ) : (
                    detail.actions.map((action) => (
                      <div key={action.action_id} className="p-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-gray-900">
                                {action.title}
                              </p>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${priorityClass(action.priority)}`}>
                                {action.priority}
                              </span>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${actionStatusClass(action.status)}`}>
                                {humanizeStatus(action.status)}
                              </span>
                            </div>
                            <p className="mt-1 text-xs leading-relaxed text-gray-600">
                              {action.description}
                            </p>
                            <p className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                              <Clock size={12} />
                              Due {formatDate(action.due_at)}
                            </p>
                          </div>
                          <div className="flex shrink-0 flex-wrap gap-2">
                            {actionStatusOptions.map((option) => (
                              <button
                                key={`${action.action_id}-${option.value}`}
                                type="button"
                                disabled={updatingAction === action.action_id}
                                onClick={() => void changeActionStatus(action.action_id, option.value)}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {updatingAction === action.action_id
                                  ? "Updating..."
                                  : option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 p-5 xl:grid-cols-2">
                <div>
                  <h3 className="mb-3 font-semibold text-gray-900">Documents</h3>
                  <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {detail.documents.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500">No documents are linked.</p>
                    ) : (
                      detail.documents.map((doc) => (
                        <div key={doc.document_id} className="p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-gray-900">
                                {doc.filename || doc.document_type}
                              </p>
                              <p className="mt-1 font-mono text-xs text-gray-500">
                                {doc.document_id}
                              </p>
                            </div>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
                              {humanizeStatus(doc.document_type)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                doc.is_latest_version
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-amber-100 text-amber-700"
                              }`}
                            >
                              v{doc.version} {doc.is_latest_version ? "Latest" : "Superseded"}
                            </span>
                          </div>
                          <p className="mt-2 text-xs text-gray-500">
                            Workflow: <span className="font-mono">{doc.workflow_id}</span>
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="mb-3 font-semibold text-gray-900">Risk Review</h3>
                  <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {detail.risks.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500">No risks are linked to this case.</p>
                    ) : (
                      detail.risks.map((risk) => (
                        <div key={risk.risk_id} className="p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle
                              size={15}
                              className={
                                risk.severity === "high"
                                  ? "mt-0.5 shrink-0 text-red-600"
                                  : "mt-0.5 shrink-0 text-amber-600"
                              }
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900">
                                {risk.summary}
                              </p>
                              <p className="mt-1 text-xs text-gray-500">
                                {risk.category} · {risk.severity} · score {Math.round(risk.score * 100)}%
                              </p>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 p-5">
                <h3 className="mb-3 font-semibold text-gray-900">Audit Timeline</h3>
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {detail.audit_events.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500">No audit events are linked.</p>
                  ) : (
                    detail.audit_events.slice(0, 8).map((event) => (
                      <div key={event.event_id} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {humanizeStatus(event.event_type)}
                          </p>
                          <p className="font-mono text-xs text-gray-500">
                            {event.workflow_id}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500">{formatDate(event.created_at)}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="border-t border-gray-100 p-5">
                <h3 className="mb-3 font-semibold text-gray-900">Case Summary</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  {detail.report_summary.length === 0 ? (
                    <li>No generated summary is available yet.</li>
                  ) : (
                    detail.report_summary.map((item, index) => (
                      <li key={`${item}-${index}`} className="rounded-lg bg-gray-50 px-3 py-2">
                        {item}
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
